import React, { useState, useEffect } from 'react';
import { 
  requestNotificationPermission, 
  getBrowserPermissionStatus, 
  setupPermissionChangeListener,
  cleanupNotificationToken
} from '../../services/FirebaseService';
import sessionManager from '../../api/SessionManager';

const NotificationPermission = () => {
  const [permissionState, setPermissionState] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  
  useEffect(() => {
    const initializeNotifications = async () => {
      console.log("=== START: NotificationPermission initialization ===");
      
      // Only proceed if user is authenticated
      if (!sessionManager.isUserAuthenticated()) {
        console.log("User not authenticated, not showing notification banner");
        setShowBanner(false);
        return;
      }
      
      // Get current browser permission status
      const currentPermission = getBrowserPermissionStatus();
      console.log("Current browser permission:", currentPermission);
      
      // Set up permission change listener
      const cleanupListener = setupPermissionChangeListener();
      
      // Clean up any invalid tokens if permission is denied
      if (currentPermission === 'denied') {
        cleanupNotificationToken();
      }
        // Check when we last showed the notification banner
      const lastPromptTime = localStorage.getItem('notification_last_prompt_time');
      const now = Date.now();
      const ONE_DAY = 1 * 1 * 1 * 3000; // 24 hours in milliseconds
      
      // If we asked less than a day ago, don't ask again
      if (lastPromptTime && (now - parseInt(lastPromptTime, 10) < ONE_DAY)) {
        console.log("Last prompt was less than a day ago, not showing banner");
        setShowBanner(false);
        return;
      }
      
      // If notifications aren't supported, don't show banner
      if (currentPermission === 'unsupported') {
        console.log("Notifications not supported, not showing banner");
        setPermissionState('unsupported');
        setShowBanner(false);
        return;
      }

      // Set permission state
      setPermissionState(currentPermission);
      
      // Show banner after a short delay
      setTimeout(() => {
        setShowBanner(true);
        // Remember when we showed the banner
        localStorage.setItem('notification_last_prompt_time', now.toString());
        console.log("Showing notification banner for authenticated user");
      }, 3000);
      
      // Add event listener for incoming messages for debugging purposes
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        console.log("Setting up notification message listener for debugging");
        
        navigator.serviceWorker.addEventListener('message', (event) => {
          console.log("=== ServiceWorker message received ===");
          console.log("ServiceWorker message data:", event.data);
          
          // Check if this is a push notification
          if (event.data && event.data.type === 'PUSH_RECEIVED') {
            console.log("Push notification received:", event.data.notification);
          }
        });
        
        // Also listen for the 'push' event directly
        navigator.serviceWorker.ready.then(registration => {
          console.log("Service worker ready, setting up debug listeners");
          
          // Log the current active service worker
          console.log("Current active service worker:", registration.active ? registration.active.scriptURL : 'None');
        });
      } else {
        console.log("ServiceWorker or PushManager not supported in this browser");
      }
      
      console.log("=== END: NotificationPermission initialization ===");
      
      // Clean up the permission change listener when component unmounts
      return cleanupListener;
    };
    
    initializeNotifications();
  }, []);
  
  const handleRequestPermission = async () => {
    console.log("=== START: handleRequestPermission ===");
    setIsLoading(true);
    
    try {
      // This will trigger the browser's native permission dialog
      const result = await requestNotificationPermission();
      console.log("Permission request result:", result);
      
      if (result.success) {
        console.log("Permission request successful, hiding banner");
        setPermissionState('granted');
        setShowBanner(false);
        // Print the device token to console if available
        if (result.token) {
          console.log("Device token:", result.token);
        }
      } else {
        // Update state based on current browser permission
        const currentPermission = getBrowserPermissionStatus();
        console.log("Permission request unsuccessful, current permission:", currentPermission);
        setPermissionState(currentPermission);
        
        // If denied, keep banner open to show instructions
        if (currentPermission === 'denied') {
          console.log("Permission denied, showing instructions");
          // We keep the banner open but with different content
        }
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsLoading(false);
      console.log("=== END: handleRequestPermission ===");
    }
  };
  
  // Don't show banner if not needed
  if (!showBanner) return null;
  
  // Function to manually check if service worker is receiving messages
  const checkForMessages = () => {
    console.log("=== Manually checking for push notification messages ===");
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        console.log("Service worker status:", registration.active ? "Active" : "Not active");
        console.log("Service worker URL:", registration.active ? registration.active.scriptURL : "None");
        
        // Log the device token from localStorage if available
        const storedToken = localStorage.getItem('fcm_token');
        console.log("Stored FCM token:", storedToken);
        
        // Check if it matches the token from the logs
        const expectedToken = "fky8cy70TMfbwEOnyIa-BC:APA91bHwEsRQ6oKNBmqFZ8STEI2iG96E8P9-crOzCeYAF2h9s1my1j-Q3SgezgcV5tA47Yk6Ge4neDYXOTpaeZT2neG2f9CwCS92ZMPkhgxfmpMVQqGTZ3I";
        console.log("Token match:", storedToken === expectedToken);
        
        // Send a message to the service worker to check communication
        if (registration.active) {
          registration.active.postMessage({
            type: 'NOTIFICATION_DEBUG',
            message: 'Checking service worker communication'
          });
          console.log("Debug message sent to service worker");
        }
      });
    } else {
      console.log("Service Worker API not available");
    }
  };
  
  // Render different content based on permission state
  if (permissionState === 'denied') {
    // For denied permissions, show instructions on how to re-enable
    return (
      <div className="fixed bottom-4 left-0 right-0 mx-auto w-full max-w-md bg-white rounded-lg shadow-xl border border-orange-200 z-50 animate-fade-in-up">
        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <div className="relative">
                <svg className="h-12 w-12 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-bold text-gray-900">Notifications are Blocked</h3>
              <p className="mt-1 text-sm text-gray-600">
                You've blocked notifications for this site. Here's how to enable them:
              </p>
              <div className="mt-4 bg-gray-50 p-3 rounded-md">
                <h4 className="font-medium text-sm text-gray-700">How to enable notifications:</h4>
                <ol className="mt-2 text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Click the lock/info icon in your browser's address bar</li>
                  <li>Find "Notifications" in the site settings</li>
                  <li>Change the setting from "Block" to "Allow"</li>
                  <li>Refresh this page</li>
                </ol>
              </div>
            </div>
            
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setShowBanner(false)}
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // For default permissions, show the normal banner
  return (
    <div className="fixed bottom-4 left-0 right-0 mx-auto w-full max-w-md bg-white rounded-lg shadow-xl border border-blue-200 z-50 animate-fade-in-up">
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 pt-0.5">
            <div className="relative">
              <svg className="h-12 w-12 text-blue-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full"></span>
            </div>
          </div>
          
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-bold text-gray-900">Get Important Updates!</h3>
            <p className="mt-1 text-sm text-gray-600">
              Enable notifications to receive tournament alerts, match schedules, and prize announcements instantly.
            </p>
            <div className="mt-4">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-3 text-sm text-blue-700">
                <p className="font-medium">You'll be prompted to allow notifications by your browser</p>
                <p className="mt-1">Please click <strong>"Allow"</strong> when your browser asks for permission</p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={handleRequestPermission}
                  disabled={isLoading || permissionState === 'denied'}
                  className={`w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-bold rounded-md shadow-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    isLoading ? 'opacity-75 cursor-not-allowed' : 'animate-pulse'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      ENABLE NOTIFICATIONS
                      <svg className="ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBanner(false)}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Maybe Later
                </button>
              </div>
              
              {/* Debug button for developers */}
              {process.env.NODE_ENV !== 'production' && (
                <div className="mt-2 text-xs">
                  <button
                    onClick={checkForMessages}
                    className="text-gray-500 underline"
                  >
                    Debug Notifications
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => setShowBanner(false)}
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermission;
